import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Select from 'common/components/select/Select';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { VehicleComparisonDashboardDto } from 'api/dashboard/dtos/VehicleComparisonDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import Utils from 'common/services/Utils';
import Logger from 'common/services/Logger';
import { DefaultAssetImage, LOGGER_LOG_TYPE } from 'Config';
import { useToasts } from 'react-toast-notifications';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import styles from './VehicleComparison.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';

type Props = {
	vehicles: SelectValueLabel[];
};

const MIN_VEHICLES = 2;
const MAX_VEHICLES = 5;
const SELECTION_STORAGE_KEY = 'fleetmax_dashboard_comparison_vehicles';

type MetricKey =
	| 'totalCost' | 'costPerKm' | 'expenses' | 'maintenanceCost'
	| 'km' | 'consumption'
	| 'maintenanceCount' | 'accidentCount'
	| 'availability' | 'immobilized';

type MetricDef = {
	key: MetricKey;
	labelKey: string;
	getRaw: (v: VehicleComparisonDashboardDto, ctx: MetricContext) => number | null;
	format: (raw: number | null) => string;
	invertBetter?: boolean;
	showBar?: boolean;
};

type MetricContext = {
	totalDays: number;
};

type SectionDef = {
	titleKey: string;
	color: string;
	metrics: MetricKey[];
};

const SECTIONS: SectionDef[] = [
	{ titleKey: 'dashboard.comparison.section.financial', color: '#02c7c7', metrics: ['totalCost', 'costPerKm', 'expenses', 'maintenanceCost'] },
	{ titleKey: 'dashboard.comparison.section.operational', color: '#038cb6', metrics: ['km', 'consumption'] },
	{ titleKey: 'dashboard.comparison.section.incidents', color: '#7c5cbf', metrics: ['maintenanceCount', 'accidentCount'] },
	{ titleKey: 'dashboard.comparison.section.availability', color: '#d4a832', metrics: ['availability', 'immobilized'] },
];

const VehicleComparison = ({ vehicles }: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [selectedVehicles, setSelectedVehicles] = useState<SelectValueLabel[]>([]);
	const [data, setData] = useState<VehicleComparisonDashboardDto[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('vehicleComparison');

	const uniqueVehicles = useMemo(() => {
		const seen = new Set<string>();
		return vehicles.filter(v => {
			if (seen.has(v.value)) return false;
			seen.add(v.value);
			return true;
		});
	}, [vehicles]);

	useEffect(() => {
		if (uniqueVehicles.length === 0 || selectedVehicles.length > 0) return;
		try {
			const raw = localStorage.getItem(SELECTION_STORAGE_KEY);
			if (!raw) return;
			const savedIds: string[] = JSON.parse(raw);
			const restored = savedIds
				.slice(0, MAX_VEHICLES)
				.map(id => uniqueVehicles.find(v => v.value === id))
				.filter((v): v is SelectValueLabel => v != null);
			if (restored.length >= MIN_VEHICLES) {
				setSelectedVehicles(restored);
			}
		} catch {
			// ignore
		}
	}, [uniqueVehicles]);

	const fetchComparison = useCallback(async (vehicleIds: string[]) => {
		if (vehicleIds.length < MIN_VEHICLES) {
			setData([]);
			return;
		}
		try {
			setIsLoading(true);
			const result = await DashboardService.getVehicleComparison(vehicleIds, dateFrom, dateTo);
			setData(result || []);
		} catch (error) {
			addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			Logger.error(LOGGER_LOG_TYPE.DASHBOARD, 'error getting vehicle comparison', error);
		} finally {
			setIsLoading(false);
		}
	}, [dateFrom, dateTo]);

	useEffect(() => {
		const ids = selectedVehicles.map(v => v.value);
		fetchComparison(ids);
	}, [selectedVehicles, fetchComparison]);

	const handleVehicleChange = (values: any) => {
		const selected: SelectValueLabel[] = values || [];
		if (selected.length > MAX_VEHICLES) return;
		setSelectedVehicles(selected);
		try {
			localStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify(selected.map(v => v.value)));
		} catch {
			// ignore
		}
	};

	const translateFuelName = (name: string) => {
		if (!name) return '';
		const key = `vehicle.fuels.${name}` as any;
		const translated = t(key) as string;
		return translated !== key ? translated : name.charAt(0) + name.slice(1).toLowerCase();
	};

	const totalDays = useMemo(() => {
		if (!dateFrom || !dateTo) return 0;
		const diff = dateTo.getTime() - dateFrom.getTime();
		return Math.max(Math.ceil(diff / (1000 * 60 * 60 * 24)), 1);
	}, [dateFrom, dateTo]);

	const na = t('common.not_applicable' as any) as string;

	const formatNumber = (value: number | null) => {
		if (value == null) return na;
		return value.toLocaleString('fr-FR', { maximumFractionDigits: 0 });
	};
	const formatConsumption = (value: number | null) => {
		if (value == null) return na;
		return value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' lt/100km';
	};
	const formatCostPerKm = (value: number | null) => {
		if (value == null) return na;
		return value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + ' €/km';
	};
	const formatCurrency = (value: number | null) => value == null ? na : Utils.formatCurrency(value);
	const formatDays = (value: number | null) => value == null ? na : `${value} d`;
	const formatPercent = (value: number | null) => value == null ? na : `${value} %`;
	const formatCount = (value: number | null) => value == null ? na : String(value);

	const getAvailability = (daysImmobilized: number): number | null => {
		if (totalDays <= 0) return null;
		return Math.max(0, Math.round(((totalDays - daysImmobilized) / totalDays) * 100));
	};

	const METRICS: Record<MetricKey, MetricDef> = useMemo(() => ({
		totalCost: {
			key: 'totalCost',
			labelKey: 'dashboard.comparison.total_cost',
			getRaw: v => v.totalCost,
			format: formatCurrency,
			invertBetter: true,
		},
		costPerKm: {
			key: 'costPerKm',
			labelKey: 'dashboard.comparison.cost_per_km',
			getRaw: v => v.costPerKm,
			format: formatCostPerKm,
			invertBetter: true,
		},
		expenses: {
			key: 'expenses',
			labelKey: 'dashboard.comparison.expenses_cost',
			getRaw: v => v.expensesCost,
			format: formatCurrency,
			invertBetter: true,
		},
		maintenanceCost: {
			key: 'maintenanceCost',
			labelKey: 'dashboard.comparison.maintenance_cost',
			getRaw: v => v.maintenanceCost,
			format: formatCurrency,
			invertBetter: true,
		},
		km: {
			key: 'km',
			labelKey: 'dashboard.comparison.km_short',
			getRaw: v => v.currentKilometers,
			format: formatNumber,
		},
		consumption: {
			key: 'consumption',
			labelKey: 'dashboard.comparison.consumption_short',
			getRaw: v => v.averageConsumption,
			format: formatConsumption,
			invertBetter: true,
		},
		maintenanceCount: {
			key: 'maintenanceCount',
			labelKey: 'dashboard.comparison.maintenance_count',
			getRaw: v => v.maintenanceCount,
			format: formatCount,
			invertBetter: true,
		},
		accidentCount: {
			key: 'accidentCount',
			labelKey: 'dashboard.comparison.accident_count',
			getRaw: v => v.accidentCount,
			format: formatCount,
			invertBetter: true,
		},
		availability: {
			key: 'availability',
			labelKey: 'dashboard.comparison.availability',
			getRaw: v => getAvailability(v.daysImmobilized),
			format: formatPercent,
		},
		immobilized: {
			key: 'immobilized',
			labelKey: 'dashboard.comparison.immobilized',
			getRaw: v => v.daysImmobilized,
			format: formatDays,
			invertBetter: true,
		},
		// eslint-disable-next-line
	}), [totalDays]);

	// Ordered data matching the selection order so column order is predictable.
	const orderedData = useMemo(() => {
		if (!data.length) return [];
		const ids = selectedVehicles.map(v => v.value);
		return ids
			.map(id => data.find(d => d.vehicleId === id))
			.filter((v): v is VehicleComparisonDashboardDto => v != null);
	}, [data, selectedVehicles]);

	const computeRowStats = (rawValues: (number | null)[], invertBetter: boolean | undefined) => {
		const defined = rawValues.filter((v): v is number => v != null);
		if (defined.length < 2) return { bestValue: null as number | null, worstValue: null as number | null, max: 0 };
		const best = invertBetter ? Math.min(...defined) : Math.max(...defined);
		const worst = invertBetter ? Math.max(...defined) : Math.min(...defined);
		return { bestValue: best, worstValue: worst, max: Math.max(...defined.map(Math.abs)) };
	};

	const getVehicleImage = (v: VehicleComparisonDashboardDto) =>
		v.photoUrl || v.vehicleSegmentDefaultPhotoUrl || DefaultAssetImage;

	const selector = (
		<div className={styles.selectorWrapper}>
			<Select
				options={uniqueVehicles}
				value={selectedVehicles}
				onChange={handleVehicleChange}
				isMulti
				isClearable
				slim
				usePortal
				placeholder={t('dashboard.comparison.select_vehicles_placeholder' as any, { min: MIN_VEHICLES, max: MAX_VEHICLES }) as string}
			/>
			<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />
		</div>
	);

	const renderBody = () => {
		if (isLoading) {
			return <div className={tableStyles.centerAlign}><LoadingSpinner /></div>;
		}
		if (selectedVehicles.length < MIN_VEHICLES) {
			return <div className={styles.emptyMessage}>{t('dashboard.comparison.select_vehicles_hint' as any, { min: MIN_VEHICLES, max: MAX_VEHICLES })}</div>;
		}
		if (orderedData.length < MIN_VEHICLES) {
			return <WidgetNoData />;
		}

		return (
			<div className={styles.tableScroll}>
				<table className={styles.compTable}>
					<thead>
						<tr>
							<th className={`${styles.cornerCell} ${styles.stickyCol}`} />
							{orderedData.map(v => (
								<th key={v.vehicleId} className={styles.vehicleHeadCell}>
									<div className={styles.vehicleHead} onClick={() => window.open(`/vehicles/details/${v.vehicleId}`, '_blank')}>
										<div className={styles.vehicleImageWrapper}>
											<div className={styles.vehicleImage} style={{ backgroundImage: `url(${getVehicleImage(v)})` }} />
											<div className={styles.registrationOverlay}>{v.registrationNumber}</div>
										</div>
									</div>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						<tr className={styles.metricRow}>
							<td className={`${styles.metricLabelCell} ${styles.stickyCol}`}>{t('dashboard.comparison.fuel_type' as any)}</td>
							{orderedData.map(v => (
								<td key={v.vehicleId} className={styles.valueCell}>
									<div className={styles.valueText}>{translateFuelName(v.fuelName) || '—'}</div>
								</td>
							))}
						</tr>
						{SECTIONS.map(section => (
							<React.Fragment key={section.titleKey}>
								{section.metrics.map(mKey => {
									const metric = METRICS[mKey];
									const raws = orderedData.map(v => metric.getRaw(v, { totalDays }));
									const { bestValue, worstValue, max } = computeRowStats(raws, metric.invertBetter);
									return (
										<tr key={mKey} className={styles.metricRow}>
											<td className={`${styles.metricLabelCell} ${styles.stickyCol}`}>{t(metric.labelKey as any)}</td>
											{orderedData.map((v, i) => {
												const raw = raws[i];
												const isBest = raw != null && bestValue != null && raw === bestValue && bestValue !== worstValue;
												const isWorst = raw != null && worstValue != null && raw === worstValue && bestValue !== worstValue;
												const barPct = metric.showBar && max > 0 && raw != null ? Math.min(100, (Math.abs(raw) / max) * 100) : 0;
												const cellClass = [
													styles.valueCell,
													isBest ? styles.valueBest : '',
													isWorst ? styles.valueWorst : '',
												].filter(Boolean).join(' ');
												return (
													<td key={v.vehicleId} className={cellClass}>
														<div className={styles.valueText}>{metric.format(raw)}</div>
														{metric.showBar && (
															<div className={styles.intensityTrack}>
																<div
																	className={styles.intensityFill}
																	style={{
																		width: `${barPct}%`,
																		backgroundColor: isBest ? '#02c7c7' : '#c8ede9',
																	}}
																/>
															</div>
														)}
													</td>
												);
											})}
										</tr>
									);
								})}
							</React.Fragment>
						))}
					</tbody>
				</table>
			</div>
		);
	};

	return (
		<Box className={tableStyles.container} title={t('dashboard.vehicle_comparison') as string} renderOptions={selector} subTitleTooltip={t('dashboard.vehicle_comparison_tooltip' as any) as string}>
			{renderBody()}
		</Box>
	);
};

export default VehicleComparison;
